import { track } from "./lib/analytics";

type ContactAdvisorProps = {
  /** Human-readable label of the step the customer is on when they reach out. */
  stepName: string;
  /** Threads the handoff onto the same GA4 record as the rest of the funnel. */
  submissionId: string;
};

type Channel = "tel" | "email";

/**
 * Persistent human-contact affordance, rendered under the widget in BOTH the
 * standalone and the embedded render.
 *
 * It exists for embed mode specifically: there the site footer (and with it both
 * phone numbers) is not rendered, so without this the iframe contains no way to
 * reach a person. A customer who cannot get their address to resolve, which is a
 * designed outcome and not a fault (see the README), would otherwise have nowhere
 * to go but close the modal.
 *
 * A native <details> disclosure, so it is keyboard-operable and screen-reader
 * labelled with no JS and no focus trap to get wrong. Phone is the primary
 * channel and both regional lines are surfaced rather than guessing which one
 * applies; email is the secondary.
 */
export function ContactAdvisor({ stepName, submissionId }: ContactAdvisorProps) {
  const onContact = (channel: Channel) => () =>
    track("contact_advisor_click", {
      step: stepName,
      channel,
      submission_id: submissionId,
    });

  return (
    <details class="contact-advisor">
      <summary>
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        <span>Talk to an advisor</span>
      </summary>
      <div class="contact-advisor-panel">
        <p class="contact-advisor-lead">Questions? Reach a Kasselman Solar advisor.</p>
        <a class="contact-advisor-line" href="tel:+15184788365" onClick={onContact("tel")}>
          <span class="contact-advisor-region">Upstate</span>
          <span class="contact-advisor-value">(518) 478-8365</span>
        </a>
        <a class="contact-advisor-line" href="tel:+18456814088" onClick={onContact("tel")}>
          <span class="contact-advisor-region">Hudson Valley</span>
          <span class="contact-advisor-value">(845) 681-4088</span>
        </a>
        <a
          class="contact-advisor-line"
          href="mailto:info@kasselmansolar.com"
          onClick={onContact("email")}
        >
          <span class="contact-advisor-region">Email</span>
          <span class="contact-advisor-value">info@kasselmansolar.com</span>
        </a>
      </div>
    </details>
  );
}
